// ./src/hooks/useDashboard.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  DashboardService,
  DashboardItem,
  DashboardParamsInput,
  Car,
} from "@/utils/api";
import { useApp, useEntityFiltersContext } from "@/contexts";
import { ApiError } from "@/utils";

// ============================================
// Types
// ============================================

export interface UseDashboardOptions {
  /**
   * Default params applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply new params, call `load(newParams)` explicitly.
   */
  initialParams?: DashboardParamsInput;

  /** Auto-load data on mount (and on userId change). @default false */
  autoLoad?: boolean;

  /** Auto-select the first car when data is loaded and no car is selected. @default true */
  autoSelectFirstCar?: boolean;
}

export interface UseDashboardReturn {
  // Data state
  items: DashboardItem[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Cars derived from dashboard items
  cars: Car[];
  totalCarsCount: number;
  hasMultipleCars: boolean;
  hasCars: boolean;
  firstCar: Car | null;

  // Active car selection (backed by EntityFiltersContext.carId)
  activeCarId: string | null;
  activeCar: Car | null;
  activeItem: DashboardItem | null;
  setActiveCarId: (carId: string | null) => void;

  // Core operations
  load: (params?: DashboardParamsInput) => Promise<OpResult>;
  refresh: () => Promise<OpResult>;
  refreshStats: () => Promise<OpResult>;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getItemByCarId: (carId: string | undefined) => DashboardItem | undefined;
  getCarById: (carId: string | undefined) => Car | undefined;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Hook Implementation
// ============================================

export const useDashboard = (
  options: UseDashboardOptions = {}
): UseDashboardReturn => {
  const {
    initialParams = {},
    autoLoad = false,
    autoSelectFirstCar = true,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // Get vehicle selection methods from EntityFiltersContext
  const {
    filters,
    persistVehicleId,
    restoreVehicleId,
  } = useEntityFiltersContext();

  // ── Mount-time configuration captured in refs ──
  const initialParamsRef = useRef(initialParams);
  const autoSelectFirstCarRef = useRef(autoSelectFirstCar);

  // Ref to track the last-queried params for refresh
  const lastQueriedParamsRef = useRef<DashboardParamsInput>(initialParams);

  // Monotonically increasing call IDs to detect stale responses.
  // Separate counters for load() and refreshStats() so they don't invalidate each other.
  const loadCallIdRef = useRef(0);
  const refreshCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Track whether vehicle restoration has been done after cars load
  const vehicleRestoredRef = useRef(false);

  // Data state
  const [items, setItems] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Active car selection — derived from EntityFiltersContext.carId
  const activeCarId = filters.carId ?? null;

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // ==========================================
  // Derived Values
  // ==========================================

  const isEmpty = useMemo(
    () => isLoaded && items.length === 0,
    [isLoaded, items.length]
  );

  // Extract cars from dashboard items
  const cars = useMemo(
    () =>
      items
        .map((item) => item.car)
        .filter((car): car is Car => car != null),
    [items]
  );

  const totalCarsCount = cars.length;
  const hasMultipleCars = cars.length > 1;
  const hasCars = cars.length > 0;
  const firstCar = cars.length > 0 ? cars[0] : null;

  // Active car and item derived from activeCarId
  const activeCar = useMemo(() => {
    if (!activeCarId) return null;
    return cars.find((car) => car.id === activeCarId) ?? null;
  }, [activeCarId, cars]);

  const activeItem = useMemo(() => {
    if (!activeCarId) return null;
    return items.find((item) => item.carId === activeCarId) ?? null;
  }, [activeCarId, items]);

  // ==========================================
  // Lookup Helpers
  // ==========================================

  const getItemByCarId = useCallback(
    (carId: string | undefined): DashboardItem | undefined => {
      if (!carId) return undefined;
      return items.find((item) => item.carId === carId);
    },
    [items]
  );

  const getCarById = useCallback(
    (carId: string | undefined): Car | undefined => {
      if (!carId) return undefined;
      return cars.find((car) => car.id === carId);
    },
    [cars]
  );

  // ==========================================
  // Active Car Selection
  // ==========================================

  /**
   * Set the active car and persist the selection to device storage.
   * Delegates to EntityFiltersContext.persistVehicleId which updates
   * the carId filter and saves to user-scoped localStorage.
   */
  const setActiveCarId = useCallback(
    (carId: string | null) => {
      persistVehicleId(carId);
    },
    [persistVehicleId]
  );

  // ==========================================
  // Vehicle Restoration & Auto-Select
  // ==========================================

  /**
   * After cars are loaded, restore the previously selected vehicle
   * from device storage. If no valid stored selection exists and
   * autoSelectFirstCar is enabled, select the first car.
   *
   * Also handles stale selection: if the current activeCarId points
   * to a car that no longer exists in the loaded list, re-run
   * restoration logic.
   */
  useEffect(() => {
    if (!isLoaded || items.length === 0) return;

    const carIds = items
      .map((item) => item.carId)
      .filter((id): id is string => !!id);

    if (carIds.length === 0) return;

    // Check if current activeCarId is stale (car removed/deactivated)
    if (activeCarId && !carIds.includes(activeCarId)) {
      // Current selection is stale — clear and re-restore
      vehicleRestoredRef.current = false;
    }

    // Only run restoration once per data load cycle
    if (vehicleRestoredRef.current) return;
    vehicleRestoredRef.current = true;

    // Try to restore from device storage
    const restoredCarId = restoreVehicleId(carIds);

    if (restoredCarId) {
      // Successfully restored — nothing more to do
      return;
    }

    // No valid stored selection — auto-select if enabled
    if (autoSelectFirstCarRef.current) {
      persistVehicleId(carIds[0]);
    }
  }, [isLoaded, items, activeCarId, restoreVehicleId, persistVehicleId]);

  // ==========================================
  // Utility
  // ==========================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    // Invalidate any in-flight calls so their responses are ignored
    loadCallIdRef.current++;
    refreshCallIdRef.current++;

    setItems([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);

    // Reset vehicle restoration tracking so it re-runs after next load
    vehicleRestoredRef.current = false;
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  /**
   * Load dashboard data.
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   *
   * Keeps previous data visible until new data arrives (no clearing on load start).
   */
  const load = useCallback(
    async (params?: DashboardParamsInput): Promise<OpResult> => {
      const callId = ++loadCallIdRef.current;

      setLoading(true);
      setLastError(null);

      // Reset vehicle restoration so it re-runs after new data loads
      vehicleRestoredRef.current = false;

      // Build params with timezone offset if not provided
      const paramsToUse: DashboardParamsInput = {
        timezoneOffset: new Date().getTimezoneOffset(),
        ...params,
      };

      // Store for refresh
      lastQueriedParamsRef.current = paramsToUse;

      try {
        const result = await DashboardService.get(paramsToUse);

        // Guard: discard stale response
        if (callId !== loadCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as DashboardItem[])
            : [];
          setItems(newItems);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to load dashboard data")
        );
        setIsLoaded(true);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== loadCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to load dashboard data")
        );
        setIsLoaded(true);
        return result;
      } finally {
        // Only update loading flag if this call is still the latest
        if (callId === loadCallIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  // Refresh dashboard data (full refresh including cars) using last-queried params
  const refresh = useCallback(async (): Promise<OpResult> => {
    return load(lastQueriedParamsRef.current);
  }, [load]);

  /**
   * Refresh stats only, keeping existing car objects intact.
   *
   * Calls the API with includeCars=false, then merges the new stats
   * into the existing items — preserving the car object from the
   * previous load. This is the lightweight refresh to call after
   * expense/refuel/revenue mutations.
   *
   * Skips if no data has been loaded yet (isLoaded=false) to avoid
   * a stats-only response replacing the initial full load.
   */
  const refreshStats = useCallback(async (): Promise<OpResult> => {
    // Don't refresh stats before initial load — there are no car objects to preserve
    if (!isLoaded) {
      return new OpResult();
    }

    const callId = ++refreshCallIdRef.current;

    // Don't show full loading state for stats-only refresh
    setLastError(null);

    const paramsToUse: DashboardParamsInput = {
      ...lastQueriedParamsRef.current,
      timezoneOffset: new Date().getTimezoneOffset(),
      includeCars: false,
    };

    try {
      const result = await DashboardService.get(paramsToUse);

      // Guard: discard stale response
      if (callId !== refreshCallIdRef.current) {
        return result;
      }

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const statsItems = Array.isArray(result.data)
          ? (result.data as DashboardItem[])
          : [];

        // Merge: keep existing car objects, replace stats
        setItems((prevItems) => {
          // Build a map of existing cars from previous items
          const carsMap = new Map<string, Car | null | undefined>();
          for (const item of prevItems) {
            carsMap.set(item.carId, item.car);
          }

          return statsItems.map((statsItem) => ({
            ...statsItem,
            // Preserve existing car object (API returns car=null when includeCars=false)
            car: carsMap.get(statsItem.carId) ?? statsItem.car,
          }));
        });

        setIsLoaded(true);
        setLastError(null);
        return result;
      }

      setLastError(
        ApiError.fromResult(result, "Failed to refresh dashboard stats")
      );
      return result;
    } catch (error) {
      if (callId !== refreshCallIdRef.current) {
        return OpResult.fromException(error);
      }

      const result = OpResult.fromException(error);
      setLastError(
        ApiError.fromException(error, "Failed to refresh dashboard stats")
      );
      return result;
    }
  }, [isLoaded]);

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        load({
          timezoneOffset: new Date().getTimezoneOffset(),
          ...initialParamsRef.current,
        });
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        load({
          timezoneOffset: new Date().getTimezoneOffset(),
          ...initialParamsRef.current,
        });
      }
    }
  }, [userId, autoLoad, load, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      items,
      loading,
      isLoaded,
      isEmpty,

      // Cars
      cars,
      totalCarsCount,
      hasMultipleCars,
      hasCars,
      firstCar,

      // Active car selection
      activeCarId,
      activeCar,
      activeItem,
      setActiveCarId,

      // Core operations
      load,
      refresh,
      refreshStats,

      // Helper operations
      reset,

      // Lookup helpers
      getItemByCarId,
      getCarById,

      // Error handling
      lastError,
      clearError,
    }),
    [
      items,
      loading,
      isLoaded,
      isEmpty,
      cars,
      totalCarsCount,
      hasMultipleCars,
      hasCars,
      firstCar,
      activeCarId,
      activeCar,
      activeItem,
      setActiveCarId,
      load,
      refresh,
      refreshStats,
      reset,
      getItemByCarId,
      getCarById,
      lastError,
      clearError,
    ]
  );
};